/**
 * Field Registry Implementation
 *
 * Purpose: Centralized registry for field type metadata and behavior
 * Created: November 1, 2025
 *
 * This registry eliminates the need for hardcoded switch statements
 * and provides a single source of truth for field type information.
 */

import type { IFieldRegistry, FieldTypeConfig } from './types';
import type {
  UnifiedFieldType,
  FieldCategory,
  FieldTypeCapabilities,
} from './field-types';
import { fieldTypeConfigs } from './config';

/**
 * Field Registry Implementation
 */
class FieldRegistry implements IFieldRegistry {
  private configs: Map<UnifiedFieldType, FieldTypeConfig>;

  constructor() {
    this.configs = new Map(
      Object.entries(fieldTypeConfigs) as [UnifiedFieldType, FieldTypeConfig][]
    );
  }

  /**
   * Get configuration for a specific field type
   */
  get(fieldType: UnifiedFieldType): FieldTypeConfig | undefined {
    return this.configs.get(fieldType);
  }

  /**
   * Get all field configurations
   */
  getAll(): FieldTypeConfig[] {
    return Array.from(this.configs.values());
  }

  /**
   * Get field configurations by category
   */
  getByCategory(category: FieldCategory): FieldTypeConfig[] {
    return this.getAll().filter(config => config.category === category);
  }

  /**
   * Check if a field type exists
   */
  has(fieldType: UnifiedFieldType): boolean {
    return this.configs.has(fieldType);
  }

  /**
   * Get HTML input type for a field type
   */
  getHtmlInputType(fieldType: UnifiedFieldType): string {
    const config = this.get(fieldType);
    return config?.htmlInputType || 'text';
  }

  /**
   * Get capabilities for a field type
   */
  getCapabilities(fieldType: UnifiedFieldType): FieldTypeCapabilities {
    const config = this.get(fieldType);
    if (!config) {
      // Return default capabilities for unknown types
      return {
        supportsValidation: false,
        supportsMinMax: false,
        requiresOptions: false,
        supportsDateFunctions: false,
        supportsFileUpload: false,
        supportsRichText: false,
        isContainer: false,
        allowsChildren: false,
      };
    }
    return config.capabilities;
  }

  /**
   * Get icon for a field type
   */
  getIcon(fieldType: UnifiedFieldType) {
    const config = this.get(fieldType);
    return config?.icon;
  }

  /**
   * Get data entry field types
   */
  getDataEntryTypes(): FieldTypeConfig[] {
    return this.getByCategory('data-entry');
  }

  /**
   * Get selection field types
   */
  getSelectionTypes(): FieldTypeConfig[] {
    return this.getByCategory('selection');
  }

  /**
   * Get layout field types
   */
  getLayoutTypes(): FieldTypeConfig[] {
    return this.getByCategory('layout');
  }

  /**
   * Get field types allowed in field definitions
   */
  getFieldDefTypes(): FieldTypeConfig[] {
    return this.getAll().filter(config => config.allowedInFieldDef);
  }

  /**
   * Get field types allowed in form builder
   */
  getFormBuilderTypes(): FieldTypeConfig[] {
    return this.getAll().filter(config => config.allowedInFormBuilder);
  }

  /**
   * Get field types by group
   */
  getFieldsByGroup(group: string): FieldTypeConfig[] {
    return this.getAll().filter(config => config.group === group);
  }

  /**
   * Get field options for context
   */
  getFieldOptions(
    context: 'fieldDef' | 'formBuilder' | 'all' = 'all'
  ): Array<{ value: UnifiedFieldType; label: string }> {
    let configs: FieldTypeConfig[];

    switch (context) {
      case 'fieldDef':
        configs = this.getFieldDefTypes();
        break;
      case 'formBuilder':
        configs = this.getFormBuilderTypes();
        break;
      case 'all':
      default:
        configs = this.getAll();
        break;
    }

    return configs.map(config => ({
      value: config.type,
      label: config.label,
    }));
  }
}

// Export singleton instance
export const fieldRegistry = new FieldRegistry();

// Export class for testing
export { FieldRegistry };
